import React from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Brain, Key, Save, Loader2 } from 'lucide-react';

const AISettingsTab = ({ orgForm, setOrgForm, handleOrgSave, saving }) => {
  return (
    <Card className="border-[#E2E6EA]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-[#00A3A3]" />
          AI Assistant Settings
        </CardTitle>
        <CardDescription>Configure Claude AI for drafting assistance</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between p-4 bg-[#F8F9FA] rounded-lg">
          <div>
            <h4 className="font-medium text-[#1A1D21]">Enable AI Assistant</h4>
            <p className="text-sm text-[#5A6570]">
              Master toggle for Claude AI assistance across the organization
            </p>
          </div>
          <Switch
            checked={orgForm.ai_enabled}
            onCheckedChange={(checked) => setOrgForm({ ...orgForm, ai_enabled: checked })}
            data-testid="ai-enabled-switch"
          />
        </div>

        {orgForm.ai_enabled && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ai-api-key">Claude API Key (Optional)</Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
                <Input
                  id="ai-api-key"
                  type="password"
                  value={orgForm.ai_api_key}
                  onChange={(e) => setOrgForm({ ...orgForm, ai_api_key: e.target.value })}
                  placeholder="sk-ant-..."
                  className="pl-10 border-[#E2E6EA]"
                  data-testid="ai-api-key-input"
                />
              </div>
              <p className="text-xs text-[#5A6570]">
                Leave blank to use the default Emergent LLM key. Provide your own key for dedicated usage.
              </p>
            </div>

            <div className="border border-[#E2E6EA] rounded-lg divide-y divide-[#E2E6EA]">
              <h4 className="font-medium text-[#1A1D21] px-4 py-3 bg-[#F8F9FA] rounded-t-lg">Feature-Level Controls</h4>
              {[
                { key: 'ai_minutes_enabled', label: 'AI-Assisted Minutes', desc: 'Generate draft minutes from meeting notes', testId: 'ai-minutes-toggle' },
                { key: 'ai_resolutions_enabled', label: 'AI-Assisted Resolutions', desc: 'Draft resolution wording from decision context', testId: 'ai-resolutions-toggle' },
                { key: 'ai_certificates_enabled', label: 'AI-Assisted Certificates', desc: "Generate Secretary's Certificate content", testId: 'ai-certificates-toggle' },
                { key: 'ai_notices_enabled', label: 'AI-Assisted Notices', desc: 'Draft meeting notices and communications', testId: 'ai-notices-toggle' },
              ].map(({ key, label, desc, testId }) => (
                <div key={key} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-[#1A1D21]">{label}</p>
                    <p className="text-xs text-[#5A6570]">{desc}</p>
                  </div>
                  <Switch
                    checked={orgForm[key] !== false}
                    onCheckedChange={(checked) => setOrgForm({ ...orgForm, [key]: checked })}
                    data-testid={testId}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={handleOrgSave} disabled={saving} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="save-ai-settings-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AISettingsTab;
